import React from "react";
import { Box, Grid } from "@mui/material";
import ExploreCardSkeleton from "./ExploreCardSkeleton";
import GalleryHeader from "../WheelGallery/Header/GalleryHeader";
import { HERO_ROW_HEIGHT, HERO_PER_PAGE_BY_BAND } from "./heroLayout";
import { HERO_PER_PAGE } from "../../pages/ExploreV2/heroLogic";
import { DISCOVER_WRAPPER_PT, DISCOVER_WRAPPER_PB } from "../../pages/ExploreV2/discoverTopSpacing";
import { DISCOVER_CARD_V2_ENABLED } from "../../constants/featureFlags";
import { useBreakpoint } from "../../utils/breakpoints";
import { colors } from "../../utils";

// DiscoverFeedSkeleton — the ROUTE-LEVEL loading affordance for /discover
// (LOADER-STUTTER fix, ISP-9999, Gordon 2026-08-23).
//
// WHY THIS EXISTS: /discover's route chunk used to load behind routes.js's shared
// <BrandWheelLoader variant="fullpage"> Suspense fallback — a full-screen brand
// WHEEL. The instant the chunk resolved, ExploreV2Page mounted and replaced that
// wheel with its OWN data-loading state, which is a grid of gray card SKELETONS
// (ExploreCardSkeleton). So a single navigation showed: full-page WHEEL ->
// discarded -> skeleton grid -> content. That wheel->skeleton SWAP is the core
// perceived stutter Gordon reported (audit: qa-notes/LOADER-STUTTER-AUDIT.md).
//
// THE FIX: /discover now suspends to THIS skeleton (the SAME ExploreCardSkeleton
// shells the page itself renders while its first data page is in flight) instead
// of to the fullpage wheel. So the sequence becomes: skeleton grid (chunk load)
// -> skeleton grid (data load) -> content — ONE continuous skeleton affordance,
// never a wheel that gets thrown away. Pick ONE loading affordance per surface.
//
// T22.516 (Kelly, Discover, Responsive): "Header info missing" + "Upon opening
// the app in Discover you will notice a flicker". Measured on web-stg
// main.16b0375e (1440 + 390, 15fps frames): this fallback was ONLY the card grid,
// pinned to the top of the viewport — no app header, no "Discover" wordmark, no
// Trending row. When the chunk resolved, ExploreV2Page's own loading frame
// (GalleryHeader + wordmark + Trending hero skeletons + category row + grid)
// replaced it in one frame, so the header popped in and the grid jumped ~200px
// down. The fallback now draws the same frame the page draws while loading:
// the REAL GalleryHeader (already in the main bundle, so no extra chunk) and the
// page's wrapper / tinted top zone / wordmark / hero row / controls row, sized
// from the page's own constants (discoverFeedSkeleton.parity.test.js pins them
// to pages/ExploreV2/index.jsx so they cannot drift apart silently).
//
// aria-hidden: the shells are decorative; a route-transition announcement is the
// RouteAnnouncer's job, and the page's own busy state is announced once it mounts.
// The header stays OUTSIDE the aria-hidden region — it is real, operable chrome.

// Mirrors the inner wrapper of pages/ExploreV2/index.jsx.
export const DISCOVER_SKELETON_WRAPPER_SX = {
  width: "100%",
  maxWidth: 1760,
  mx: "auto",
  px: { xs: 2, sm: 3, md: 4 },
  pt: DISCOVER_WRAPPER_PT,
  pb: DISCOVER_WRAPPER_PB,
  boxSizing: "border-box",
};

// Mirrors the tinted Trending/header zone of pages/ExploreV2/index.jsx.
export const DISCOVER_SKELETON_TOP_ZONE_SX = {
  ...(DISCOVER_CARD_V2_ENABLED && {
    mx: { xs: -2, sm: -3, md: -4 },
    px: { xs: 2, sm: 3, md: 4 },
    pt: 1,
    background: `linear-gradient(180deg, ${colors.PrimaryPurple100} 0%, ${colors.PrimaryPurple50} 45%, ${colors.white} 100%)`,
  }),
};

export const DISCOVER_SKELETON_CONTROLS_MIN_HEIGHT = { xs: 116, sm: 56, md: 52 };

const DiscoverFeedSkeleton = () => {
  const { bp } = useBreakpoint();
  const heroPerPage = HERO_PER_PAGE_BY_BAND[bp] || HERO_PER_PAGE;
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.white }}>
      <GalleryHeader showCreateWheel />
      <Box
        aria-hidden="true"
        data-testid="discover-feed-skeleton"
        sx={DISCOVER_SKELETON_WRAPPER_SX}
      >
        <Box sx={DISCOVER_SKELETON_TOP_ZONE_SX}>
          {/* Wordmark: same type ramp as the page's <h1>, but not a heading —
              the page's own h1 is the one the document outline should carry. */}
          <Box
            data-testid="discover-feed-skeleton-wordmark"
            sx={{
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: 22, sm: 26, md: 30 },
              fontWeight: 700,
              lineHeight: 1.15,
              width: "fit-content",
              background: `linear-gradient(90deg, ${colors.PrimaryPurple500} 0%, ${colors.primary} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: colors.PrimaryPurple600 || colors.primary,
            }}
          >
            Discover
          </Box>
          {/* Trending today: label line + one row of hero shells, the same
              row height / per-band count ExploreHero uses while loading. */}
          <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
            <Box sx={{ height: 22, mb: 1.25 }} />
            <Box
              data-testid="discover-feed-skeleton-hero"
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${heroPerPage}, minmax(0, 1fr))`,
                gap: 2,
                gridAutoRows: HERO_ROW_HEIGHT,
              }}
            >
              {Array.from({ length: heroPerPage }).map((_, i) => (
                <ExploreCardSkeleton hero key={`hero-skeleton-${i}`} />
              ))}
            </Box>
          </Box>
        </Box>
        {/* Category pills + search/sort row (wraps to two rows below sm).
            Heights measured on the page's own loading frame (web-stg,
            border-box incl. its 4px top padding): 390 -> 116, 768 -> 56,
            1440 -> 52. */}
        <Box
          sx={{
            pt: 0.5,
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            minHeight: DISCOVER_SKELETON_CONTROLS_MIN_HEIGHT,
          }}
        />
        <Grid container spacing={2}>
          <ExploreCardSkeleton count={12} />
        </Grid>
      </Box>
    </Box>
  );
};

export default DiscoverFeedSkeleton;
